import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FaEdit, FaTrash, FaBook } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { courseAPI, bookAPI } from '../../../../utils/api';
import CourseCreate from '../../create/js/courseCreate';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';

export default function CourseTable({ onCreate, reloadKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [, setBooksMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([courseAPI.getAll(), bookAPI.getAll()]);
      const map = {};
      (b.data || []).forEach(x => { map[x.id] = x; });
      setBooksMap(map);
      
      const list = (Array.isArray(c.data) ? c.data : []).map(r => {
        const bookArr = r.Books || r.books || [];
        return {
          ...r,
          Books: bookArr,
          _bookNames: bookArr.length ? bookArr.map(x => x.name).join(', ') : '-',
        };
      });
      setRows(list);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [reloadKey]);

  const remove = async (id) => {
    const res = await Swal.fire({
      title: 'Delete Course?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#e74c3c',
    });
    if (res.isConfirmed) {
      await courseAPI.delete(id);
      await load();
      Swal.fire({ icon: 'success', title: 'Deleted', toast: true, position: 'top-end', timer: 3000 });
    }
  };

  const exportCsv = async () => {
    try {
      const res = await courseAPI.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'courses_export.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { Swal.fire({ icon:'error', title:'Export failed', text:e.message }); }
  };

  const importCsv = async (csv) => {
    const res = await courseAPI.importCsv(csv);
    await load();
    return res.data;
  };

  const downloadSample = async () => {
    try {
      const res = await courseAPI.sampleCsv();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'courses_sample.csv'; a.click(); URL.revokeObjectURL(url);
    } catch {
      console.error('Sample download failed');
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { 
      accessorKey: 'name', 
      header: 'Course Name', 
      cell: ({ getValue }) => <span style={{ fontWeight: 600, color: '#2d3436' }}>{getValue()}</span> 
    },
    { 
      accessorKey: '_bookNames', 
      header: 'Associated Books',
      cell: ({ getValue }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaBook style={{ color: '#0f6fad', fontSize: '0.8rem' }} />
          <span style={{ fontSize: '0.9rem', color: '#636e72' }}>{getValue()}</span>
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn edit" onClick={() => setEditRow(row.original)}><FaEdit /></button>
          <button className="eduss-global-action-btn delete" onClick={() => remove(row.original.id)}><FaTrash /></button>
        </div>
      ),
    },
  ];

  const filteredData = rows.filter(r => 
    !filter || 
    r.name?.toLowerCase().includes(filter.toLowerCase()) ||
    r._bookNames?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar 
        title="Courses Management"
        subtitle="Manage academic courses and their textbook associations"
        onAdd={onCreate}
        onExport={exportCsv}
        onImport={importCsv}
        onDownloadSample={downloadSample}
        onSearch={setFilter}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
          enableSelection={true}
        />
      </div>

      {editRow && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Update Course</h3>
              <button onClick={() => setEditRow(null)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <CourseCreate course={editRow} onClose={() => setEditRow(null)} onSubmit={async () => { await load(); setEditRow(null); }} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

CourseTable.propTypes = {
  onCreate: PropTypes.func,
  reloadKey: PropTypes.number
};
